import { appendJsonHeader, RestRequestBuilder } from "@frui.ts/apiclient";
import { deserialize, plainToClass } from "class-transformer";
import { ClassConstructor } from "class-transformer";
import { interfaces } from "inversify";
import { apiUrl, apiVersion } from "../config";
import AuthorizationService from "../services/authorizationService";
import ApiConnector from "./backendConnector";
import { IResults } from "./types";
import { RequestBuilderConfiguration } from "./eshopRequestBuilder";

export default class DeserializingRequestBuilder extends RestRequestBuilder {
  constructor(authorizationService: AuthorizationService, configuration?: RequestBuilderConfiguration) {
    super(ApiConnector, `${configuration?.customApiUrl || apiUrl}${configuration?.customApiVersion || apiVersion}`, {
      headers: {
        ...authorizationService.buildHeaders(),
      },
    });
  }

  getEntities<T>(returnType: ClassConstructor<T>, queryParams?: any): Promise<IResults<T>> {
    const requestUrl = this.appendQuery(this.url, queryParams);
    const params = appendJsonHeader(this.params);
    return this.apiConnector
      .get(requestUrl, params)
      .then(x => x.json())
      .then(x => {
        return {
          items: x.items.map((item: any) => plainToClass(returnType, item)),
        };
      });
  }

  getEntity<T>(returnType: ClassConstructor<T>, queryParams?: any): Promise<T> {
    const requestUrl = this.appendQuery(this.url, queryParams);
    const params = appendJsonHeader(this.params);
    return this.apiConnector
      .get(requestUrl, params)
      .then(x => x.text())
      .then(x => deserialize(returnType, x));
  }

  postEntity<T>(content: any, returnType: ClassConstructor<T>): Promise<T> {
    const params = appendJsonHeader(this.params);
    return this.apiConnector
      .postJson(this.url, content, params)
      .then(x => x.text())
      .then(x => deserialize(returnType, x));
  }

  putEntity<T>(content: any, returnType: ClassConstructor<T>): Promise<T> {
    const params = appendJsonHeader(this.params);
    return this.apiConnector
      .putJson(this.url, content, params)
      .then(x => x.text())
      .then(x => deserialize(returnType, x));
  }

  patchEntity<T>(content: any, returnType: ClassConstructor<T>): Promise<T> {
    const params = appendJsonHeader(this.params);
    return this.apiConnector
      .patchJson(this.url, content, params)
      .then(x => x.text())
      .then(x => deserialize(returnType, x));
  }

  static Factory({ container }: interfaces.Context) {
    return (configuration?: RequestBuilderConfiguration) =>
      new DeserializingRequestBuilder(container.get(AuthorizationService), configuration);
  }
}
